/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Section } from 'ui/molecules/Section/Section';

import styles from './BusinessCreditRiskMonitoring.module.scss';

import {
  getTriggerRows,
  STARTING_CHANGE_COUNT,
  TRIGGER_FEED,
  WATCHED_ENTITIES,
} from './BusinessCreditRiskMonitoring.data';

type FeedEvent = (typeof TRIGGER_FEED)[number] & { fireId: number };

const FIRE_INTERVAL = 2600;

export const BusinessCreditRiskMonitoring = () => {
  const { t } = useInt();
  const triggerRows = getTriggerRows(t);

  const panelRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(panelRef, { continuous: true, threshold: 0.15 });

  const [events, setEvents] = useState<(FeedEvent | null)[]>(() => Array(triggerRows.length).fill(null));
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [count, setCount] = useState(STARTING_CHANGE_COUNT);

  const feedIndexRef = useRef(0);
  const fireIdRef = useRef(0);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setEvents((prev) => {
        const next = [...prev];
        const seen = new Set<number>();

        TRIGGER_FEED.forEach((event) => {
          if (seen.has(event.row)) {
            return;
          }

          seen.add(event.row);
          fireIdRef.current += 1;
          next[event.row] = { ...event, fireId: fireIdRef.current };
        });

        return next;
      });

      return undefined;
    }

    const id = setInterval(() => {
      const event = TRIGGER_FEED[feedIndexRef.current % TRIGGER_FEED.length];
      feedIndexRef.current += 1;
      fireIdRef.current += 1;

      setEvents((prev) => {
        const next = [...prev];
        next[event.row] = { ...event, fireId: fireIdRef.current };
        return next;
      });
      setActiveRow(event.row);
      setCount((current) => current + 1 + Math.floor(Math.random() * 3));
    }, FIRE_INTERVAL);

    return () => clearInterval(id);
  }, [visible]);

  return (
    <Section
      className={styles.root}
      title={t('The file keeps moving after you approve the limit')}
      description={t(
        'Put a portfolio under watch and the changed field arrives on the day it is picked up, with the filing that triggered it.',
      )}
      align="left"
    >
      <div className={styles.panel} ref={panelRef}>
        <div className={styles.panelHead}>
          <span className={styles.label}>{t('What triggers an alert')}</span>
          <span className={styles.liveTag}>
            <i className={styles.liveDot} />
            <b>{count}</b> {t('changes across')} {WATCHED_ENTITIES} {t('watched entities this week')}
          </span>
        </div>

        <div className={styles.cols}>
          <span>{t('Type of change')}</span>
          <span>{t('Checked')}</span>
          <span>{t('What changed')}</span>
        </div>

        <div className={styles.rows}>
          {triggerRows.map((row, i) => {
            const event = events[i];

            return (
              <div key={row.name} className={cn(styles.row, activeRow === i && styles.rowActive)}>
                <span className={styles.rowName}>{row.name}</span>
                <span className={styles.rowCadence}>{row.cadence}</span>
                <span className={styles.rowEvent}>
                  {event && (
                    <span key={event.fireId} className={styles.rowEventText}>
                      <b>{event.who}</b> &middot; {event.change}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};
